import {
  AggregateRatingProductSchemaProps,
  ImageInfo,
  QuestionsConfig,
  ReviewMinimized,
  ReviewProductSchemaProps,
  ReviewRatingQuantity,
} from './types/Review'
import { createUrlParams } from '../lib/params'
import fetcher from './fetcher'
import { parseResponse } from '../services/useApiHelpers'
import { Order } from './types/Product'
import { ResourceName } from './types/Resource'
import questionsConfigJson from './reviewQuestionConfig.json'
import { REVIEWS_PER_PAGE_AGGREGATION } from './constants'

const apiUrl = process.env.NEXT_PUBLIC_API_URL
const reviewUrl = `${apiUrl}/jsonapi/default/review`
const reviewImgURL = process.env.NEXT_PUBLIC_REVIEW_IMG_URL
const orderUrl = `${apiUrl}/jsonapi/default/order`

export const getLatestOrderAndLatestUnreviewedOrder = async (
  parentProdId: string,
): Promise<[Order | undefined, Order | undefined]> => {
  const include: ResourceName[] = ['order/product']
  const commonParams = {
    filter: {
      '||': [
        {
          '==': {
            field: 'order.product.parentproductid',
            value: parentProdId,
          },
        },
        {
          '==': {
            field: 'order.product.productid',
            value: parentProdId,
          },
        },
      ],
    },
    include,
  }

  const queryParamsLatestOrder = {
    ...commonParams,
    page: {
      limit: '1',
    },
  }

  const queryParamsLatestUnreviewedOrder = {
    ...commonParams,
    page: {
      limit: '1',
    },
  }

  const urlParamsLatestOrder = createUrlParams(queryParamsLatestOrder)
  const urlParamsLatestUnreviewedOrder = createUrlParams(queryParamsLatestUnreviewedOrder)

  const latestOrderUrl = `${orderUrl}?${urlParamsLatestOrder}`
  const latestUnreviewedOrderUrl = `${orderUrl}?${urlParamsLatestUnreviewedOrder}`

  const latestOrderResp = await fetcher(latestOrderUrl)
  const latestOrderRespData = await latestOrderResp.json()
  const { data: latestOrders } = parseResponse<Order>(latestOrderRespData)
  const latestOrder: Order | undefined = latestOrders[0]

  const latestUnreviewedOrderResp = await fetcher(latestUnreviewedOrderUrl)
  const latestUnreviewedOrderRespData = await latestUnreviewedOrderResp.json()
  const { data: latestUnreviewedOrders } = parseResponse<Order>(
    latestUnreviewedOrderRespData,
  )
  const latestUnreviewedOrder: Order | undefined = latestUnreviewedOrders[0]

  return [latestOrder, latestUnreviewedOrder]
}

export const reviewRatingListItemKeys = ['a', 'b', 'c', 'd', 'e']

export const getTotalOfReviews = (quantities: ReviewRatingQuantity[]) => quantities
  .reduce((a, b) => a + b['review.rating.value'], 0)

export const getAverageOfReviews = (quantities: ReviewRatingQuantity[]) => (quantities.length
  ? quantities
    .map((quantity) => (Number(quantity.id ?? '0')) * quantity['review.rating.value'])
    .reduce((a, b) => a + b, 0)
  / getTotalOfReviews(quantities)
  : 0)

export const getImageURLs = (review: ReviewMinimized) => {
  const allQuestions: QuestionsConfig = questionsConfigJson as QuestionsConfig

  let imageURLs: { url: string, title: string }[] = []
  const questions = {
    ...review['review.data']['review.data.questions'],
    ...review['review.data']['review.data.conditionalquestions'] ?? {},
  }

  if (questions) {
    Object.keys(questions).forEach((key) => {
      if (allQuestions[key] && allQuestions[key].type === 'file' && questions[key].toString().length) {
        imageURLs = [
          ...imageURLs,
          ...(questions[key] as ImageInfo[])
            .map((image) => ({
              url: `${reviewImgURL}/${review['review.id']}/${image.url}`,
              title: `imageAttachment-${image.name}`,
            }))]
      }
    })
  }

  return imageURLs
}

export const convertReviewToProductSchemaVersion = (
  review: ReviewMinimized,
): ReviewProductSchemaProps => ({
  author: review['review.name'] || 'Anonymous',
  datePublished: review['review.submitdate'] ?? '',
  reviewBody: review['review.comment'] ?? '',
  name: review['review.comment'] ?? '',
  reviewRating: {
    worstRating: '1',
    ratingValue: String(
      Math.max(1, Math.min(review['review.rating'], 5)), // Force the rating between 1 and 5
    ),
    bestRating: '5',
  },
  publisher: {
    type: review['review.data']['review.data.company'] ? 'Organization' : 'Person',
    name: review['review.data']['review.data.company'] ?? review['review.name'],
  },
})

export const getAggregatedRatingsForProductSchema = (
  ratingQuantities: ReviewRatingQuantity[],
): AggregateRatingProductSchemaProps => {
  const average = getAverageOfReviews(ratingQuantities)
  const total = getTotalOfReviews(ratingQuantities)

  return {
    ratingValue: average.toFixed(1),
    reviewCount: total.toFixed(0),
  }
}

export const getReviewFilterParams = (productId: string) => ({
  filter: {
    '&&': [
      {
        '==': {
          field: 'review.parentprodid',
          value: productId,
        },
      },
      {
        '==': { // Filters out guest reviews (domain === 'guest-product)
          field: 'review.domain',
          value: 'product',
        },
      },
      {
        '!': [ // Filters out unfinished reviews
          {
            '~=': {
              field: 'review.data',
              value: '%22review.data.label%22%3A%22incomplete',
            },
          },
        ],
      },
    ],
  },
})

/** Returns a rating quantity array with missing ratings filled in with quantity 0, sorted by
 * rating
*/
export const fillMissingRatingQuantities = (ratingQuantities: ReviewRatingQuantity[]) => {
  // Reviews with rating of 0 (i.e. unfinished reviews) are not displayed
  const validReviewQuantities = ratingQuantities
    .filter(({ id }) => !!id && id !== 0)
  const foundRatings = validReviewQuantities.map(({ id }) => id)
  const allRatings = [1, 2, 3, 4, 5]
  const missingRatings = Array.from(allRatings.filter((rating) => !foundRatings.includes(rating)))
  // Fill out missing quantity objects
  validReviewQuantities.push(...missingRatings.map((rating) => ({
    id: rating,
    'review.rating.value': 0,
  })))

  return validReviewQuantities.sort((a, b) => Number(b.id) - Number(a.id))
}

export const fetchReviewQuantities = async (
  productId: string,
): Promise<ReviewRatingQuantity[]> => {
  const filterParams = getReviewFilterParams(productId)
  const reviewRatingQueryAggregationParams = createUrlParams({
    ...filterParams,
    page: {
      limit: REVIEWS_PER_PAGE_AGGREGATION,
    },
    other: {
      aggregate: 'review.rating',
    },
  })

  const reviewUrlAggregation = `${reviewUrl}?${reviewRatingQueryAggregationParams}`
  const ratingQuantityResp = await fetcher(reviewUrlAggregation)
  const respData = await ratingQuantityResp.json()

  if (respData.data?.length) {
    const { data: ratingQuantities } = parseResponse<ReviewRatingQuantity>(respData)

    return fillMissingRatingQuantities(ratingQuantities)
  }

  return []
}

export const getReviewUrlParams = (
  productId: string,
  pageLimit: number,
  pageOffset?: number,
) => createUrlParams({
  ...getReviewFilterParams(productId),
  page: {
    limit: String(pageLimit),
    offset: String(pageOffset ?? 0),
  },
  sort: '-review.submitdate', /** Newest first, BE pagination is inconsistent when sorting by
  rating. TODO: Fix rating-based review sorting by setting submitdate as tie-breaker */
})

export const fetchReviews = async (
  productId: string,
  pageLimit: number,
  pageOffset?: number,
): Promise<ReviewMinimized[]> => {
  const reviewUrlWithQueryParams = `${reviewUrl}?${getReviewUrlParams(productId, pageLimit, pageOffset)}`

  const reviewResp = await fetcher(reviewUrlWithQueryParams)
  const respData = await reviewResp.json()
  const { data: newReviews } = parseResponse<ReviewMinimized>(respData)

  return newReviews
}
